import React from 'react'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { Maybe } from '@graphql-tools/utils'
import classNames from 'classnames'
import { GuildHighResShieldIcon } from '@/atoms/Icons/GuildHighResShieldIcon'
import { ResponsiveBackgroundImage } from '@/atoms/ResponsiveBackgroundImage'
import { AsH1, AsSpan, HeadingXS, ParagraphSM } from '@/atoms/Text'
import { ScrollingTitles } from '@/organisms/ScrollingTitles'
import { options } from '@/services/ContentfulService'
import { isGuildSignupScrollingLayout } from '@/services/GuildSignupPageService/validations'
import { useTranslate } from '@/utils/translate/translate-client'
import { GuildEmailInput } from '@/views/GuildJoinView/GuildEmailInput'
import { useGuildSignupContext } from '@/views/GuildSignupView/GuildSignupContext'
import { AlreadyAGuildMember } from '@/views/GuildSignupView/GuildSingleShowLayout/GuildHeroCTA/AlreadyAGuildMember'

interface GuildScrollingHeroProps {
  hasUser?: boolean
  hideAlreadyAGuildMember?: boolean
}
export const GuildScrollingHero: React.FC<GuildScrollingHeroProps> = ({ hasUser, hideAlreadyAGuildMember }) => {
  const { layout, sale } = useGuildSignupContext()

  if (!isGuildSignupScrollingLayout(layout)) {
    return null
  }

  const scrollingLayout = layout

  return (
    <div className="relative">
      <ScrollingTitleHero
        paragraph={documentToReactComponents(scrollingLayout?.paragraph?.json, options)}
        heading={scrollingLayout?.heading}
        backgroundHeroImage={scrollingLayout?.backgroundHeroImage}
        hideAlreadyAGuildMember={hideAlreadyAGuildMember}
        hasUser={hasUser}
        hasSale={!!sale}
      />
      <div
        className="absolute inset-0 h-full w-full"
        style={{ background: 'linear-gradient(180deg, rgba(20, 20, 20, 0.00) 0%, #222222 100%)' }}
      />
    </div>
  )
}

interface ScrollingTitleHeroProps {
  heading?: React.ReactNode | string
  backgroundHeroImage: Maybe<string>
  paragraph: React.ReactNode | string
  hasUser?: boolean
  hideAlreadyAGuildMember?: boolean
  hasSale?: boolean
}

export const ScrollingTitleHero: React.FC<ScrollingTitleHeroProps> = ({
  heading,
  backgroundHeroImage,
  paragraph,
  hideAlreadyAGuildMember,
  hasUser,
  hasSale,
}) => {
  const { t } = useTranslate('guild')
  const { experiments } = useGuildSignupContext()
  const isWatchNowSignupPageCta = Boolean(experiments?.['get-started-signup-page-cta'])

  return (
    <section
      aria-label={typeof heading === 'string' ? heading : undefined}
      className="2xl:max-h-auto relative h-full min-h-screen w-full overflow-hidden"
    >
      {backgroundHeroImage && (
        <ResponsiveBackgroundImage
          src={backgroundHeroImage}
          className="pointer-events-none touch-none select-none"
          transforms="q_auto:low"
        />
      )}
      <div
        className={classNames(
          'h-screen min-h-screen absolute left-0 top-0 z-10 w-full bg-transparent px-4 md:max-h-[800px] md:px-8 xl:mx-[40px] xl:px-4',
          { 'mt-16 md:mt-0': hasSale },
        )}
      >
        <div className="relative flex h-full w-full flex-col-reverse items-center justify-center lg:!flex-row 2xl:mx-auto 2xl:max-w-[1600px]">
          <div className="-mt-auto flex h-full w-full flex-col items-start justify-center gap-y-4 bg-transparent py-0 opacity-100 md:items-center md:gap-y-6 lg:-mt-40 lg:w-1/2 lg:items-start lg:py-32 2xl:pl-10">
            <div>
              <div className="flex flex-col items-center gap-y-1">
                <GuildHighResShieldIcon className="invisible h-0 w-0 md:visible md:h-full md:w-fit" size={64} />
                <GuildHighResShieldIcon
                  className="invisible h-0 w-0 sm:visible sm:h-full sm:w-fit md:invisible md:h-0 md:w-0"
                  size={48}
                />
                <ParagraphSM color="white" className="hidden uppercase md:flex">
                  {t('guild', 'Guild')}
                </ParagraphSM>
              </div>
            </div>
            <div className="z-20 -mt-[80px] flex max-w-[600px] flex-col gap-y-4 md:mt-0 se:max-w-full ">
              <HeadingXS
                as={AsH1}
                weight="bold"
                className="se:photon-title-lg md:photon-heading-lg 2xl:photon-heading-xl w-full text-balance bg-transparent text-center md:text-left"
                color="white"
              >
                {heading}
              </HeadingXS>
              <ParagraphSM
                as={AsSpan}
                className="[&>p]:photon-paragraph-sm md:[&>p]:photon-paragraph-md se:[&>p]:photon-caption-sm 2xl:[&>p]:photon-paragraph-xl mb-3 w-full text-balance bg-transparent text-center md:text-left"
                color="core-gray-400"
              >
                {paragraph}
              </ParagraphSM>
            </div>
            <div className="flex w-full max-w-[600px] flex-col gap-4 pb-[120px] se:pb-[200px]">
              <GuildEmailInput
                hasUser={hasUser}
                isCentered={false}
                className="w-full"
                inputContainerClassName="!px-0"
                buttonTextOverride={isWatchNowSignupPageCta ? 'Watch Now' : undefined}
              />
              <AlreadyAGuildMember
                className={classNames('text-center text-[14px] text-core-gray-300 lg:text-left', {
                  'hidden lg:block': hideAlreadyAGuildMember,
                })}
                linkClassName="text-core-oxide relative underline cursor-pointer"
              />
            </div>
          </div>
          <ScrollingTitles />
        </div>
      </div>
    </section>
  )
}
